import * as React from 'react';

/**
 * One clock for every skeleton on screen.
 *
 * A page that loads in steps shows several skeletons one after another (the
 * shell's, the route's fallback, the page's own). Each one is a new element, so
 * its CSS animations start from zero: the fade-in goes back to opacity 0 after
 * its 100 ms delay and the shimmer jumps back to its first frame. Seen from the
 * outside that is a blink on every handover.
 *
 * This hook pins the animations of a skeleton to a shared start time. While at
 * least one skeleton is alive, or the last one left less than `GAP_MS` ago (a
 * replacement unmounts the old one and mounts the new one in the same commit),
 * a new skeleton adopts the existing start: it continues at the same point of
 * the fade and the same phase of the shimmer. After a real gap (content showed
 * up and a later load needs a skeleton again) the clock restarts, so a first
 * load keeps its 100 ms grace and does not flash.
 *
 * It only writes `Animation.startTime`; the keyframes stay in kit-next.css.
 * Where the Web Animations API is missing (jsdom) or the page timeline is not
 * available it does nothing and the CSS plays on its own.
 */
const GAP_MS = 200;

let alive = 0;
let startedAt: number | null = null;
let lastGone = 0;

function timelineNow(): number | null {
  const now = typeof document === 'undefined' ? undefined : document.timeline?.currentTime;
  return typeof now === 'number' ? now : null;
}

/** Returns a ref for the skeleton's root element. */
export function useSkeletonClock<T extends Element>() {
  const ref = React.useRef<T>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    const now = timelineNow();
    if (!el || now === null || typeof el.getAnimations !== 'function') return;

    if (alive === 0 && (startedAt === null || now - lastGone > GAP_MS)) startedAt = now;
    alive++;
    for (const animation of el.getAnimations()) animation.startTime = startedAt;

    return () => {
      alive--;
      if (alive === 0) lastGone = timelineNow() ?? now;
    };
  }, []);

  return ref;
}

/** Test hook: back to a clean clock. */
export function resetSkeletonClock() {
  alive = 0;
  startedAt = null;
  lastGone = 0;
}
